import React from 'react'
import { MDBCard, MDBCardBody, MDBIcon } from 'mdb-react-ui-kit'

import './EventCard.component.scss'

/**
 * Truncate text to a specified length with ellipsis.
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string}
 */
const truncateText = (text, maxLength = 120) => {
  if (!text) return ''
  // Strip HTML tags for preview
  let stripped = text.replace(/<[^>]*>/g, '')
  stripped = stripped
    .replace(/\\n/g, '<br />') // matches backslash + n from DB like "\\n"
    .replace(/\n/g, '<br />') // matches actual newline characters
  if (stripped.length <= maxLength) return stripped
  return stripped.substring(0, maxLength).trim() + '...'
}

/**
 * EventCard Component
 *
 * Displays a single event in a card format with color accent,
 * date badge, title, and description preview.
 *
 * @param {Object} props
 * @param {Object} props.event - Event data object
 * @param {Function} props.onClick - Click handler for the card
 * @param {boolean} props.isPast - Whether this is a past event
 */
const EventCard = ({ event, onClick, isPast = false }) => {
  const {
    summary,
    description,
    eventColorHex,
    isMultiDay,
    displayStartDate,
    displayEndDate,
    displayStartTime,
    displayEndTime,
    startDay,
    startMonth,
  } = event

  const handleClick = () => {
    if (onClick) onClick(event)
  }

  const handleKeyPress = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      handleClick()
    }
  }

  /**
   * Format the date/time display based on whether event is multi-day.
   */
  const formatDateTime = () => {
    if (isMultiDay) {
      return `${displayStartDate} - ${displayEndDate}`
    }
    return `${displayStartDate} · ${displayStartTime} - ${displayEndTime}`
  }

  return (
    <MDBCard
      className={`EventCardComponent ${isPast ? 'past-event' : ''}`}
      onClick={handleClick}
      onKeyPress={handleKeyPress}
      tabIndex={0}
      role='button'
      aria-label={`View details for ${summary}`}
    >
      <div className='event-color-bar' style={{ backgroundColor: eventColorHex }} />
      <div className='event-header'>
        <div className='date-badge' style={{ backgroundColor: eventColorHex }}>
          <span className='badge-day'>{startDay}</span>
          <span className='badge-month'>{startMonth}</span>
        </div>
        {isPast && (
          <div className='past-badge'>
            <span>Past Event</span>
          </div>
        )}
      </div>
      <MDBCardBody className='event-content'>
        <h5 className='event-title'>{summary}</h5>
        <div className='event-datetime'>
          <MDBIcon icon='calendar-alt' className='datetime-icon' />
          <span>{formatDateTime()}</span>
        </div>
        {description && (
          <p className='event-description' dangerouslySetInnerHTML={{ __html: truncateText(description) }} />
        )}
        <div className='event-action'>
          <span className='learn-more'>
            View Details <MDBIcon icon='arrow-right' />
          </span>
        </div>
      </MDBCardBody>
    </MDBCard>
  )
}

export default EventCard
